import { Flex, Form } from "antd"

import ButtonComponent from "../../../../../Components/Button"
import InputComponent from "../../../../../Components/Input"
import InputNumberComponent from "../../../../../Components/Input/InputNumber"
import Icon from "../../../../../Icon"

// Up to 8 digits before the decimal point, two after (CHA1-3031 validation).
const MAX_PRICE = 99999999.99

const defaultValues = { free_count: 0 }

const PRICE_FIELDS = [
  { name: "banking_app_price", label: "Banking app price ($)" },
  { name: "call_center_price", label: "Call Center price ($)" },
  { name: "mastercom_price", label: "Mastercom price ($)" }
]

const REQUIRED_MESSAGE = "Required fields were not filled."

// Every rule carries its own message so the error node can name its field; a form-wide
// `validateMessages.required` has no way to know which field failed.
const fieldError = (field, children) => (
  <span data-testid={`lookup-pricing-modal-${field}-error`}>{children}</span>
)

const addErrorIcon = (error, field) =>
  fieldError(
    field,
    <>
      <Icon type={"alert_triangle_error"} /> {error}
    </>
  )

const fieldLabel = (field, text) => (
  <span data-testid={`lookup-pricing-modal-${field}-label`}>{text}</span>
)

// The lookup counterpart of PricingForm: always fixed-price, three channel prices and one shared
// free-lookup allowance.
function LookupPricingForm({ onClose, onSubmit, initialValues, pricingNames, isDefault }) {
  const [form] = Form.useForm()

  const validateUnique = (_, value) => {
    // Case-sensitive, like the backend: "enterprise" and "Enterprise" are different schemes.
    const name = value?.trim()
    const isOwnName = initialValues?.name === name

    if (name && !isOwnName && pricingNames?.includes(name)) {
      return Promise.reject(
        fieldError("name", `Pricing with the name '${value.trim()}' already exists.`)
      )
    }

    return Promise.resolve()
  }

  const onFocusHandler = (e) => {
    const field = e.target.name || e.target.id

    if (field) form.setFields([{ name: field, errors: [] }])
  }

  const onFinish = ({ name, ...values }) => onSubmit({ ...values, name: name.trim() })

  return (
    <Form
      className={"with-default-error"}
      onFinish={onFinish}
      validateTrigger="onSubmit"
      layout="vertical"
      form={form}
      initialValues={initialValues || defaultValues}
    >
      <Form.Item
        name="name"
        label={fieldLabel("name", "Name")}
        rules={[
          { required: true, whitespace: true, message: addErrorIcon(REQUIRED_MESSAGE, "name") },
          {
            max: 255,
            message: addErrorIcon("Name can not be longer than 255 characters.", "name")
          },
          { validator: validateUnique }
        ]}
      >
        <InputComponent
          placeholder="Enter a new pricing scheme name"
          name="name"
          disabled={isDefault}
          onFocus={onFocusHandler}
          data-testid="lookup-pricing-modal-name-input"
        />
      </Form.Item>

      <Form.Item
        label={fieldLabel("free_count", "Number of the first lookups to skip")}
        name="free_count"
        rules={[{ required: true, message: addErrorIcon(REQUIRED_MESSAGE, "free_count") }]}
      >
        <InputNumberComponent
          id="free_count"
          data-testid="lookup-pricing-modal-free_count-input"
          placeholder="Enter the number of lookups"
          onFocus={onFocusHandler}
          min={0}
          step={1}
          precision={0}
          style={{ width: "100%" }}
        />
      </Form.Item>

      {PRICE_FIELDS.map(({ name, label }) => (
        <Form.Item
          key={name}
          label={fieldLabel(name, label)}
          name={name}
          rules={[{ required: true, message: addErrorIcon(REQUIRED_MESSAGE, name) }]}
        >
          <InputNumberComponent
            id={name}
            data-testid={`lookup-pricing-modal-${name}-input`}
            placeholder="Price e.g $1.00"
            onFocus={onFocusHandler}
            min={0.01}
            max={MAX_PRICE}
            step={0.5}
            precision={2}
            style={{ width: "100%" }}
          />
        </Form.Item>
      ))}

      <Flex gap={16} justify="end" style={{ padding: "0 3px 3px 3px" }}>
        <ButtonComponent
          size="large"
          htmlType="button"
          onClick={onClose}
          data-testid="lookup-pricing-modal-cancel-button"
        >
          Cancel
        </ButtonComponent>

        <ButtonComponent
          size="large"
          type="primary"
          htmlType="submit"
          data-testid="lookup-pricing-modal-submit-button"
        >
          {initialValues ? "Save changes" : "Create pricing scheme"}
        </ButtonComponent>
      </Flex>
    </Form>
  )
}

export default LookupPricingForm
